import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import { useAuthContext } from "../../../context/AuthContext";
import { useS3Upload } from "../../../hooks/useS3Upload";
import { IMAGE_SIZE_HINTS } from "../../../constants/institutionalIconOptions";

export default function ContentImageField({
  label,
  image = "",
  imageAlt = "",
  onChange,
  variant = "content",
  uploadSubpath = "contents",
  showAlt = true,
}) {
  const inputRef = useRef(null);
  const { userData } = useAuthContext();
  const { uploadOne } = useS3Upload();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showManual, setShowManual] = useState(Boolean(image && !image.startsWith("http")));

  const hint = IMAGE_SIZE_HINTS[variant] || IMAGE_SIZE_HINTS.content;
  const maxMb = hint.maxMb || 5;

  const prefix = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const workspace = userData?.id_workspace || "default";
    return `workspace-${workspace}/${uploadSubpath}/${yyyy}/${mm}`;
  }, [userData?.id_workspace, uploadSubpath]);

  const doUpload = async (file) => {
    if (!file) return;
    if (!/^image\//i.test(file.type)) {
      setError("Envie um arquivo de imagem (JPG, PNG ou WebP).");
      return;
    }
    if (file.size > maxMb * 1024 * 1024) {
      setError(`A imagem deve ter no máximo ${maxMb} MB.`);
      return;
    }
    try {
      setError(null);
      setBusy(true);
      const { url } = await uploadOne(file, { prefix });
      onChange({ image: url, imageAlt });
    } catch (e) {
      setError(e?.message || "Não foi possível enviar a imagem. Tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  const onPick = () => inputRef.current?.click();

  const removeImage = () => {
    onChange({ image: "", imageAlt });
    setShowManual(false);
  };

  return (
    <Box sx={{ mb: 2 }}>
      {label ? (
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        {hint.dimensions}. Formatos: JPG, PNG ou WebP (até {maxMb} MB).
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {busy ? <LinearProgress sx={{ mb: 1.5 }} /> : null}

      <Box
        sx={{
          border: "1px dashed",
          borderColor: image ? "primary.light" : "divider",
          borderRadius: 1,
          p: 2,
          mb: 1.5,
          bgcolor: image ? "grey.50" : "background.paper",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", sm: 160 },
            height: 100,
            borderRadius: 1,
            bgcolor: "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {image ? (
            <Box
              component="img"
              src={image}
              alt={imageAlt || "Pré-visualização"}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <ImageRoundedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
          )}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {image ? "Imagem selecionada. Você pode trocar ou remover." : "Nenhuma imagem ainda."}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<CloudUploadRoundedIcon />}
              onClick={onPick}
              disabled={busy}
            >
              {image ? "Trocar imagem" : "Enviar imagem"}
            </Button>
            {image ? (
              <Button
                size="small"
                color="inherit"
                startIcon={<DeleteOutlineRoundedIcon />}
                onClick={removeImage}
                disabled={busy}
              >
                Remover
              </Button>
            ) : null}
            <Button size="small" onClick={() => setShowManual((s) => !s)} disabled={busy}>
              {showManual ? "Ocultar link" : "Colar link da imagem"}
            </Button>
          </Box>
        </Box>
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) doUpload(f);
          e.target.value = "";
        }}
      />

      <Collapse in={showManual}>
        <TextField
          fullWidth
          size="small"
          label="Link da imagem (avançado)"
          value={image || ""}
          onChange={(e) => onChange({ image: e.target.value, imageAlt })}
          placeholder="https://... ou /images/..."
          helperText="Use só se a imagem já estiver hospedada em outro lugar."
          sx={{ mb: 1 }}
        />
      </Collapse>

      {showAlt ? (
        <TextField
          fullWidth
          size="small"
          label="Descrição da imagem (acessibilidade)"
          value={imageAlt || ""}
          onChange={(e) => onChange({ image, imageAlt: e.target.value })}
          helperText="Descreva o que aparece na foto para leitores de tela. Ex.: Ônibus da frota na estrada."
        />
      ) : null}
    </Box>
  );
}
